import * as THREE from 'three';
import { FullscreenPass, makeTarget } from '../gpu';

// Short wind waves and ripples (0.3 - 3 m) as an animated, tileable slope
// texture. Wave vectors are integer multiples of the tile's fundamental so
// the texture tiles exactly; every component disperses with its own speed so
// the pattern evolves instead of scrolling.

export const DETAIL_TILE = 21;
const N = 64;

const FRAG = /* glsl */ `
uniform vec4 uW[${N}];   // kx, kz, amplitude, phase
in vec2 vUv;
void main() {
  vec2 p = vUv * ${DETAIL_TILE.toFixed(1)};
  vec2 slope = vec2(0.0);
  float h = 0.0;
  for (int i = 0; i < ${N}; i++) {
    vec4 w = uW[i];
    float th = dot(w.xy, p) - w.w;
    float s = sin(th);
    h += w.z * cos(th);
    slope -= w.xy * w.z * s;
  }
  gl_FragColor = vec4(slope, h, 1.0);
}
`;

export class DetailWaves {
  readonly target = makeTarget(256, 256, {
    wrapS: THREE.RepeatWrapping,
    wrapT: THREE.RepeatWrapping,
    minFilter: THREE.LinearMipmapLinearFilter,
    generateMipmaps: true,
  });
  private readonly pass: FullscreenPass;
  private readonly kx = new Float64Array(N);
  private readonly kz = new Float64Array(N);
  private readonly omega = new Float64Array(N);
  private readonly phase0 = new Float64Array(N);
  private readonly lambda = new Float64Array(N);
  private readonly vecs: THREE.Vector4[] = [];

  constructor() {
    let seed = 1234567;
    const rand = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };
    const used = new Set<string>();
    let i = 0;
    while (i < N) {
      const t = (i + rand()) / N;
      const mag = 7 * Math.pow(70 / 7, t);
      const ang = rand() * Math.PI * 2;
      const n = Math.round(Math.cos(ang) * mag);
      const m = Math.round(Math.sin(ang) * mag);
      const key = `${n},${m}`;
      if ((n === 0 && m === 0) || used.has(key)) continue;
      used.add(key);
      const k = (2 * Math.PI) / DETAIL_TILE;
      this.kx[i] = n * k;
      this.kz[i] = m * k;
      const kk = Math.hypot(this.kx[i]!, this.kz[i]!);
      this.lambda[i] = (2 * Math.PI) / kk;
      // Gravity-capillary dispersion.
      this.omega[i] = Math.sqrt(9.81 * kk + (0.074 / 1000) * kk * kk * kk);
      this.phase0[i] = rand() * Math.PI * 2;
      this.vecs.push(new THREE.Vector4());
      i++;
    }
    this.pass = new FullscreenPass(FRAG, { uW: { value: this.vecs } });
  }

  update(renderer: THREE.WebGLRenderer, time: number, windSpeed: number, windX: number, windZ: number): void {
    const f = THREE.MathUtils.clamp(windSpeed / 9, 0.12, 1.25);
    for (let i = 0; i < N; i++) {
      const kk = Math.hypot(this.kx[i]!, this.kz[i]!);
      const c = (this.kx[i]! * windX + this.kz[i]! * windZ) / kk;
      const dir = c > 0 ? 0.25 + 0.75 * c * c : 0.25 * (1 + c);
      // Constant steepness per band (saturated short-wave spectrum).
      const amp = this.lambda[i]! * 0.016 * f * Math.sqrt(dir);
      let ph = (this.omega[i]! * time + this.phase0[i]!) % (Math.PI * 2);
      if (ph < 0) ph += Math.PI * 2;
      this.vecs[i]!.set(this.kx[i]!, this.kz[i]!, amp, ph);
    }
    this.pass.render(renderer, this.target);
  }
}
